import React from 'react';
import { IoIosArrowForward } from 'react-icons/io';
import { redirectApp } from '../core/utils/helper';

const TESTIMONIALS = [
  {
    id: 'sarah',
    name: 'Sarah M.',
    role: 'HR Recruiters',
    initials: 'SM',
    avatarBg: 'bg-blue-100',
    avatarText: 'text-blue-700',
    quote:
      "The AI tools on this platform have completely transformed how I approach my daily tasks. It's intuitive, powerful, and incredibly helpful.",
  },
  {
    id: 'sophia',
    name: 'Sophia G.',
    role: 'Digital Marketers',
    initials: 'SG',
    avatarBg: 'bg-gray-100',
    avatarText: 'text-gray-700',
    quote:
      "The platform's versatility is incredible. Whether I need help with marketing, writing, or planning, there's always a perfect tool.",
  },
  {
    id: 'luna',
    name: 'Luna H.',
    role: 'Graphic Designers',
    initials: 'LH',
    avatarBg: 'bg-yellow-100',
    avatarText: 'text-yellow-700',
    quote:
      'The creative AI tools have unleashed my artistic potential. I can now bring ideas to life faster than ever before.',
  },
  {
    id: 'robert',
    name: 'Robert K.',
    role: 'Prompt Engineers',
    initials: 'RK',
    avatarBg: 'bg-green-100',
    avatarText: 'text-green-700',
    quote:
      "It's a great platform for learning and using AI. AI-PRO is a great tool for anyone looking to learn and use AI. Nice work!",
  },
  {
    id: 'maya',
    name: 'Maya P.',
    role: 'Business Analysts',
    initials: 'MP',
    avatarBg: 'bg-purple-100',
    avatarText: 'text-purple-700',
    quote:
      'The research assistance tools have saved me countless hours. I can now focus on analysis rather than data gathering.',
  },
];

function StarRating() {
  return (
    <div className="flex gap-0.5" aria-label="5 out of 5 stars">
      {Array.from({ length: 5 }, (_, index) => (
        <svg
          key={index}
          className="w-[24px] h-[24px]"
          viewBox="0 0 20 20"
          fill="#10B981"
          aria-hidden="true"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
}

function TestimonialCard({ name, role, initials, avatarBg, avatarText, quote }) {
  return (
    <article className="flex flex-col rounded-2xl border border-[#E5E7EB] bg-white p-[24px] h-full min-h-[248px]">
      <div className="flex items-center gap-3">
        <div
          className={`flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full text-[16px] font-semibold font-inter ${avatarBg} ${avatarText}`}
        >
          {initials}
        </div>
        <div className="min-w-0 text-left">
          <p className="text-black text-base font-semibold font-inter">{name}</p>
          <p className="text-[#6B7280] text-sm font-normal font-inter">{role}</p>
        </div>
      </div>

      <hr className="my-[16px] border-[#E5E7EB]" />

      <p className="flex-1 text-[#0F1125] text-[16px] font-normal font-inter leading-tight">
        {quote}
      </p>

      <div className="mt-5">
        <StarRating />
      </div>
    </article>
  );
}

function CtaCard() {
  const handleExplore = () => {
    // document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth' });
    window.location.href = 'https://chat.ai-pro.org/';
  };

  return (
    <article className="flex flex-col rounded-2xl border border-[#E5E7EB] bg-white p-[24px] h-full min-h-[248px]">
      <h3 className="text-black text-[32px] font-bold font-inter leading-[38px]">
        What They Say After Using Our Product
      </h3>
      <p className="mt-3 text-[#6B7280] text-sm sm:text-base font-normal font-inter leading-relaxed">
        Many of our members have started their early careers with us.
      </p>

      <div className="flex flex-wrap gap-3 pt-[16px]">
        <button
          type="button"
          onClick={(e) => redirectApp(e, 'registration')}
          className="inline-flex leading-[19px] items-center justify-center rounded-full bg-[#3073D5] px-[16px] py-[12px] text-[16px] font-semibold text-white transition hover:bg-[#2590EE]"
        >
          Join Now
          <IoIosArrowForward className="ml-1 text-base" />
        </button>
        <button
          type="button"
          onClick={handleExplore}
          className="inline-flex items-center justify-center rounded-full border border-[#3073D5]
          bg-white pl-[16px] pr-[12px] text-sm sm:text-base font-semibold text-[#3073D5] transition hover:bg-[#3073D5]
          hover:text-white"
        >
          Explore
          <IoIosArrowForward className="ml-1 text-base" />
        </button>
      </div>
    </article>
  );
}

function TestimonialMarquee() {
  const renderTrack = (keyPrefix, ariaHidden = false) => (
    <ul
      className="flex shrink-0 gap-4 sm:gap-[24px] animate-infinite-scroll-r2 [&_li]:shrink-0"
      aria-hidden={ariaHidden || undefined}
    >
      {[...TESTIMONIALS, ...TESTIMONIALS].map((testimonial, index) => (
        <li key={`${keyPrefix}-${testimonial.id}-${index}`} className="w-[409px]">
          <TestimonialCard {...testimonial} />
        </li>
      ))}
    </ul>
  );

  return (
    <div className="relative flex-1 min-w-0 overflow-hidden">
      <div className="pointer-events-none absolute top-0 left-0 z-10 h-full w-12 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute top-0 right-0 z-10 h-full w-12 bg-gradient-to-l from-white to-transparent" />
      <div className="group w-full overflow-hidden">
        <div className="inline-flex w-full flex-nowrap group-hover:[&_ul]:[animation-play-state:paused] motion-reduce:[&_ul]:animate-none">
          {renderTrack('track-a')}
          {renderTrack('track-b', true)}
        </div>
      </div>
    </div>
  );
}

function TestimonialMarqueeReverse() {
  const renderTrack = (keyPrefix, ariaHidden = false) => (
    <ul
      className="flex shrink-0 gap-4 sm:gap-[24px] animate-infinite-scroll-l2 [&_li]:shrink-0"
      aria-hidden={ariaHidden || undefined}
    >
      {[...TESTIMONIALS, ...TESTIMONIALS].map((testimonial, index) => (
        <li key={`${keyPrefix}-${testimonial.id}-${index}`} className="w-[409px]">
          <TestimonialCard {...testimonial} />
        </li>
      ))}
    </ul>
  );

  return (
    <div className="relative flex-1 min-w-0 overflow-hidden">
      <div className="pointer-events-none absolute top-0 left-0 z-10 h-full w-12 bg-gradient-to-r from-white to-transparent" />
      <div className="pointer-events-none absolute top-0 right-0 z-10 h-full w-12 bg-gradient-to-l from-white to-transparent" />
      <div className="group w-full overflow-hidden">
        <div className="inline-flex w-full flex-nowrap group-hover:[&_ul]:[animation-play-state:paused] motion-reduce:[&_ul]:animate-none">
          {renderTrack('track-a')}
          {renderTrack('track-b', true)}
        </div>
      </div>
    </div>
  );
}

export default function IndexHome6() {
  return (
    <section className="bg-[#F9FCFC] px-4 sm:px-8 md:px-12 lg:px-20 py-16 sm:py-20 lg:py-24 pt-16">
      <div className="max-w-[1248px] mx-auto">
        <div className="text-center mb-12 sm:mb-14">
          <h2 className="text-black text-3xl sm:text-4xl lg:text-[48px] font-semibold font-inter">
            Our Happy Customers
          </h2>
          <p className="mt-4 text-[#6B7280] text-sm sm:text-base font-normal font-inter max-w-2xl mx-auto leading-relaxed">
            Discover how we&apos;ve helped people like you achieve their goals — straight from the
            voices of our satisfied users.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-4 sm:gap-[24px] items-stretch mb-6">
          <div className="lg:w-[360px] xl:w-[400px] shrink-0 self-stretch lg:block hidden">
            <CtaCard />
          </div>
          <TestimonialMarquee />
          <div className="lg:w-[360px] xl:w-[400px] shrink-0 self-stretch block lg:hidden">
            <CtaCard />
          </div>
        </div>
        <TestimonialMarqueeReverse />
      </div>
    </section>
  );
}
